/**
 * Video Card Component
 * Handles video playback for Shopify-hosted videos only
 * YouTube/Vimeo videos are embedded directly with native players
 */
class VideoCard extends HTMLElement {
  constructor() {
    super();
    this.playButton = null;
    this.thumbnail = null;
    this.video = null;
    this.template = null;
    this.videoType = this.dataset.videoType;
    this.isPlaying = false;
  }

  connectedCallback() {
    // Only handle Shopify videos
    if (this.videoType !== 'shopify') return;

    this.playButton = this.querySelector('.video-card__play-btn');
    this.thumbnail = this.querySelector('.video-card__thumbnail');
    this.template = this.querySelector('template');

    if (this.playButton) {
      this.playButton.addEventListener('click', this.handlePlayClick.bind(this));
    }

    // Add keyboard accessibility
    if (this.playButton) {
      this.playButton.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handlePlayClick();
        }
      });
    }
  }

  handlePlayClick() {
    if (this.isPlaying) {
      this.pauseVideo();
    } else {
      this.playVideo();
    }
  }

  playVideo() {
    if (!this.template) return;

    // Set loading state
    this.dataset.loading = 'true';

    // Clone template content
    const content = this.template.content.cloneNode(true);
    this.video = content.querySelector('video');

    if (this.video) {
      this.setupShopifyVideo();
    }
  }

  setupShopifyVideo() {
    // Add video to DOM
    this.querySelector('.video-card__media').appendChild(this.video);

    // Wait for video to be ready
    const playPromise = this.video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Video started playing successfully
          this.onVideoStarted();
        })
        .catch((error) => {
          console.error('Video play failed:', error);
          this.onVideoError();
        });
    } else {
      // Older browsers
      this.onVideoStarted();
    }

    // Add event listeners
    this.video.addEventListener('ended', () => {
      if (!this.video.loop) {
        this.pauseVideo();
      }
    });

    this.video.addEventListener('pause', () => {
      if (!this.video.ended) {
        this.pauseVideo();
      }
    });
  }

  onVideoStarted() {
    this.dataset.loading = 'false';
    this.dataset.playing = 'true';
    this.isPlaying = true;
  }

  onVideoError() {
    this.dataset.loading = 'false';
    alert('Sorry, this video could not be played. Please try again later.');
  }

  pauseVideo() {
    if (!this.video) return;

    if (this.video.pause) {
      this.video.pause();
    }

    this.dataset.playing = 'false';
    this.isPlaying = false;
  }

  disconnectedCallback() {
    if (this.playButton) {
      this.playButton.removeEventListener('click', this.handlePlayClick);
    }

    if (this.video) {
      if (this.video.pause) {
        this.video.pause();
      }
      if (this.video.parentNode) {
        this.video.remove();
      }
    }
  }
}

// Register custom element
if (!customElements.get('video-card')) {
  customElements.define('video-card', VideoCard);
}

/**
 * Intersection Observer for lazy loading videos
 * Preload videos when they come into viewport
 */
if ('IntersectionObserver' in window) {
  const videoCards = document.querySelectorAll('video-card');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const videoCard = entry.target;
          // Optionally preload video here
          observer.unobserve(videoCard);
        }
      });
    },
    {
      rootMargin: '50px',
      threshold: 0.1
    }
  );

  videoCards.forEach((card) => {
    observer.observe(card);
  });
}

/**
 * Handle mobile scroll behavior
 * Add scroll indicators if needed
 */
function initMobileScroll() {
  const grids = document.querySelectorAll('.video-cards-grid[data-enable-scroll-mobile="true"]');

  grids.forEach((grid) => {
    let isScrolling = false;
    let scrollTimeout;

    grid.addEventListener('scroll', () => {
      if (!isScrolling) {
        grid.style.scrollBehavior = 'smooth';
        isScrolling = true;
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrolling = false;
      }, 150);
    });
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileScroll);
} else {
  initMobileScroll();
}

/**
 * Pause Shopify videos when user scrolls away from section
 * Good for performance and user experience
 * Note: YouTube/Vimeo iframes manage their own playback
 */
function setupScrollPause() {
  const sections = document.querySelectorAll('.video-cards-section');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Section is out of view, pause all Shopify videos
          const playingVideos = entry.target.querySelectorAll('video-card[data-video-type="shopify"][data-playing="true"]');
          playingVideos.forEach((videoCard) => {
            videoCard.pauseVideo();
          });
        }
      });
    },
    {
      threshold: 0
    }
  );

  sections.forEach((section) => {
    observer.observe(section);
  });
}

// Initialize scroll pause
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupScrollPause);
} else {
  setupScrollPause();
}
